import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Brain, 
    Shield, 
    AlertTriangle, 
    TrendingUp, 
    TrendingDown,
    Activity,
    Lock,
    Network,
    FileText,
    RefreshCw,
    CheckCircle,
    XCircle,
    Clock,
    Zap,
    Target,
    Sparkles,
    Key
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
    getAIDashboardInsights,
    getAIPasswordAnalysis,
    getAINetworkAnalysis,
    getAIComplianceReport,
    getAIAlertsPriority
} from '../lib/api';
import { toast } from 'sonner';

const AIInsightsPanel = () => {
    const { user } = useAuth();
    const [insights, setInsights] = useState(null);
    const [passwordAnalysis, setPasswordAnalysis] = useState(null);
    const [networkAnalysis, setNetworkAnalysis] = useState(null);
    const [complianceReport, setComplianceReport] = useState(null);
    const [prioritizedAlerts, setPrioritizedAlerts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [refreshing, setRefreshing] = useState(false);

    const fetchInsights = async () => {
        try {
            const response = await getAIDashboardInsights();
            setInsights(response.data);
        } catch (error) {
            console.error('Failed to fetch AI insights:', error);
            toast.error('Failed to load AI insights. Try refreshing.');
        }
    };

    const fetchPasswordAnalysis = async () => {
        try {
            const response = await getAIPasswordAnalysis();
            setPasswordAnalysis(response.data);
        } catch (error) {
            console.error('Failed to fetch password analysis:', error);
            toast.error('Failed to load password analysis');
        }
    };

    const fetchNetworkAnalysis = async () => {
        try {
            const response = await getAINetworkAnalysis();
            setNetworkAnalysis(response.data);
        } catch (error) {
            console.error('Failed to fetch network analysis:', error);
            toast.error('Failed to load network analysis');
        }
    };

    const fetchComplianceReport = async () => {
        try {
            const response = await getAIComplianceReport();
            setComplianceReport(response.data);
        } catch (error) {
            console.error('Failed to fetch compliance report:', error);
            toast.error('Failed to load compliance report');
        }
    };

    const fetchPrioritizedAlerts = async () => {
        try {
            const response = await getAIAlertsPriority();
            setPrioritizedAlerts(response.data);
        } catch (error) {
            console.error('Failed to fetch prioritized alerts:', error);
            toast.error('Failed to load prioritized alerts');
        }
    };

    const fetchAllData = async () => {
        setLoading(true);
        await Promise.all([
            fetchInsights(),
            fetchPasswordAnalysis(),
            fetchNetworkAnalysis(),
            fetchComplianceReport(),
            fetchPrioritizedAlerts()
        ]);
        setLoading(false);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAllData();
        setRefreshing(false);
    };

    const [autoLoaded, setAutoLoaded] = useState(false);

    useEffect(() => {
        // Auto-load AI insights after a short delay (doesn't block page render)
        if (user && !autoLoaded) {
            setAutoLoaded(true);
            // Small delay to let the page render first
            const timer = setTimeout(() => {
                setLoading(true);
                fetchInsights().then(() => {
                    setLoading(false);
                    // Load others in background
                    fetchPasswordAnalysis();
                    fetchNetworkAnalysis();
                    fetchComplianceReport();
                    fetchPrioritizedAlerts();
                });
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [user, autoLoaded]);

    const handleLoadInsights = async () => {
        setLoading(true);
        setAutoLoaded(true);
        await fetchInsights();
        setLoading(false);
        // Load others in background
        fetchPasswordAnalysis();
        fetchNetworkAnalysis();
        fetchComplianceReport();
        fetchPrioritizedAlerts();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'healthy': return 'bg-green-500';
            case 'attention_needed': return 'bg-yellow-500';
            case 'at_risk': return 'bg-orange-500';
            case 'critical': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'healthy': return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
            case 'attention_needed': return <Badge className="bg-yellow-100 text-yellow-800">Attention Needed</Badge>;
            case 'at_risk': return <Badge className="bg-orange-100 text-orange-800">At Risk</Badge>;
            case 'critical': return <Badge className="bg-red-100 text-red-800">Critical</Badge>;
            default: return <Badge variant="secondary">Unknown</Badge>;
        }
    };

    const getSeverityBadge = (severity) => {
        switch (severity) {
            case 'critical': return <Badge className="bg-red-100 text-red-800">Critical</Badge>;
            case 'high': return <Badge className="bg-orange-100 text-orange-800">High</Badge>;
            case 'medium': return <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>;
            case 'low': return <Badge className="bg-green-100 text-green-800">Low</Badge>;
            default: return <Badge variant="secondary">{severity}</Badge>;
        }
    };

    const getGradeBadge = (grade) => {
        const colors = {
            'A': 'bg-green-100 text-green-800',
            'B': 'bg-blue-100 text-blue-800',
            'C': 'bg-yellow-100 text-yellow-800',
            'D': 'bg-orange-100 text-orange-800',
            'F': 'bg-red-100 text-red-800'
        };
        return <Badge className={colors[grade] || 'bg-gray-100 text-gray-800'}>{grade}</Badge>;
    };

    if (loading) {
        return (
            <Card className="col-span-full">
                <CardContent className="flex items-center justify-center py-12">
                    <div className="flex items-center space-x-3">
                        <Brain className="h-8 w-8 animate-pulse text-primary" />
                        <div>
                            <p className="text-lg font-medium">AI is analyzing your infrastructure...</p>
                            <p className="text-sm text-muted-foreground">This may take 15-30 seconds</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!insights && !passwordAnalysis && !networkAnalysis && !complianceReport && !prioritizedAlerts) {
        return (
            <Card className="border-border">
                <CardContent className="p-6 text-center">
                    <Brain className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-muted-foreground">AI insights unavailable. Click refresh to retry.</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={handleRefresh}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    // Show "Load AI Insights" button if no insights loaded yet
    if (!insights && !autoLoaded) {
        return (
            <Card className="col-span-full">
                <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg">
                        <Brain className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-xl font-bold">AI-Powered Insights</h3>
                        <p className="text-muted-foreground mt-1">Get predictive analysis, smart alerts, and security recommendations</p>
                    </div>
                    <Button onClick={handleLoadInsights} size="lg" data-testid="load-ai-btn">
                        <Sparkles className="h-5 w-5 mr-2" />
                        Load AI Insights
                    </Button>
                    <p className="text-xs text-muted-foreground">Powered by GPT-5.2 • Takes 15-30 seconds</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* AI Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg">
                        <Brain className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold flex items-center gap-2">
                            AI Insights
                            <Sparkles className="h-5 w-5 text-yellow-500" />
                        </h2>
                        <p className="text-sm text-muted-foreground">Powered by GPT-5.2</p>
                    </div>
                </div>
                <Button 
                    onClick={handleRefresh} 
                    variant="outline" 
                    disabled={refreshing}
                    data-testid="refresh-ai-btn"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Analyzing...' : 'Refresh Analysis'}
                </Button>
            </div>

            {/* Overview Cards */}
            {insights && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-primary">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Security Score</p>
                                    <p className="text-3xl font-bold">{insights.security_score || 0}</p>
                                </div>
                                <Shield className="h-10 w-10 text-primary opacity-20" />
                            </div>
                            <Progress value={insights.security_score || 0} className="mt-3" />
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-green-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Overall Status</p>
                                    <div className="mt-1">
                                        {getStatusBadge(insights.overall_status)}
                                    </div>
                                </div>
                                <Activity className="h-10 w-10 text-green-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-orange-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Top Priorities</p>
                                    <p className="text-3xl font-bold">{insights.top_priorities?.length || 0}</p>
                                </div>
                                <Target className="h-10 w-10 text-orange-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-l-4 border-l-blue-500">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Quick Wins</p>
                                    <p className="text-3xl font-bold">{insights.quick_wins?.length || 0}</p>
                                </div>
                                <Zap className="h-10 w-10 text-blue-500 opacity-20" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* AI Headline */}
            {insights?.headline && (
                <Card className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
                    <CardContent className="py-4">
                        <p className="text-lg font-medium text-center">{insights.headline}</p>
                    </CardContent>
                </Card>
            )}

            {/* Tabs for detailed insights */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="overview" data-testid="tab-overview">
                        <Brain className="h-4 w-4 mr-2" />
                        Overview
                    </TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alerts">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Alerts
                    </TabsTrigger>
                    <TabsTrigger value="passwords" data-testid="tab-passwords">
                        <Lock className="h-4 w-4 mr-2" />
                        Passwords
                    </TabsTrigger>
                    <TabsTrigger value="network" data-testid="tab-network">
                        <Network className="h-4 w-4 mr-2" />
                        Network
                    </TabsTrigger>
                    <TabsTrigger value="compliance" data-testid="tab-compliance">
                        <FileText className="h-4 w-4 mr-2" />
                        Compliance
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    {/* Password Actions Required - Show at top if any */}
                    {insights?.password_actions_required?.length > 0 && (
                        <Card className="border-2 border-red-500 bg-red-50 dark:bg-red-950">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
                                    <Key className="h-5 w-5" />
                                    Immediate Password Changes Required
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {insights.password_actions_required.map((action, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-white dark:bg-gray-900 rounded border">
                                            <div>
                                                <p className="font-medium">{action.account}</p>
                                                <p className="text-sm text-muted-foreground">{action.reason}</p>
                                            </div>
                                            <Badge className={
                                                action.urgency === 'immediate' ? 'bg-red-500 text-white' :
                                                action.urgency === 'within_24h' ? 'bg-orange-500 text-white' :
                                                'bg-yellow-500 text-black'
                                            }>
                                                {action.urgency === 'immediate' ? 'NOW' : 
                                                 action.urgency === 'within_24h' ? '24 Hours' : '1 Week'}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Top Priorities */}
                        <Card className="md:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Target className="h-5 w-5 text-orange-500" />
                                    Top Priorities
                                </CardTitle>
                                <CardDescription>Issues requiring immediate attention</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {insights?.top_priorities?.length > 0 ? (
                                    <div className="space-y-4">
                                        {insights.top_priorities.map((priority, idx) => (
                                            <div key={idx} className="p-4 bg-muted rounded-lg border-l-4 border-l-orange-500">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div className="flex-1">
                                                        <p className="font-bold text-lg">{priority.title}</p>
                                                        <p className="text-sm text-muted-foreground mt-1">{priority.description}</p>
                                                    </div>
                                                    {getSeverityBadge(priority.urgency)}
                                                </div>
                                                
                                                {/* Affected Accounts */}
                                                {priority.affected_accounts?.length > 0 && (
                                                    <div className="mt-3 p-2 bg-red-50 dark:bg-red-950 rounded">
                                                        <p className="text-sm font-medium text-red-700 dark:text-red-300 flex items-center gap-1">
                                                            <AlertTriangle className="h-4 w-4" />
                                                            Affected Accounts:
                                                        </p>
                                                        <ul className="mt-1 space-y-1">
                                                            {priority.affected_accounts.map((account, i) => (
                                                                <li key={i} className="text-sm font-mono bg-white dark:bg-gray-900 px-2 py-1 rounded">
                                                                    {account}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {/* Passwords to Change */}
                                                {priority.passwords_to_change?.length > 0 && (
                                                    <div className="mt-3 p-2 bg-orange-50 dark:bg-orange-950 rounded">
                                                        <p className="text-sm font-medium text-orange-700 dark:text-orange-300 flex items-center gap-1">
                                                            <Key className="h-4 w-4" />
                                                            Passwords to Reset Immediately:
                                                        </p>
                                                        <ul className="mt-1 space-y-1">
                                                            {priority.passwords_to_change.map((pwd, i) => (
                                                                <li key={i} className="text-sm font-mono bg-white dark:bg-gray-900 px-2 py-1 rounded flex items-center gap-2">
                                                                    <Lock className="h-3 w-3 text-orange-500" />
                                                                    {pwd}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                
                                                {/* Containment Steps */}
                                                {priority.containment_steps?.length > 0 && (
                                                    <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-950 rounded">
                                                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                                            Containment Steps:
                                                        </p>
                                                        <ol className="mt-1 space-y-1 list-decimal list-inside">
                                                            {priority.containment_steps.map((step, i) => (
                                                                <li key={i} className="text-sm">{step}</li>
                                                            ))}
                                                        </ol>
                                                    </div>
                                                )}
                                                
                                                {priority.action && (
                                                    <p className="text-sm mt-3 text-primary font-medium">→ {priority.action}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No priorities identified</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Quick Wins */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-yellow-500" />
                                    Quick Wins
                                </CardTitle>
                                <CardDescription>Easy improvements you can make today</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {insights?.quick_wins?.length > 0 ? (
                                    <ul className="space-y-2">
                                        {insights.quick_wins.map((win, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                                                <span>{win}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No quick wins available</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Trends */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5 text-green-500" />
                                    Trends
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {insights?.trends?.improving?.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                                                <TrendingUp className="h-4 w-4" /> Improving
                                            </p>
                                            <ul className="mt-1 space-y-1">
                                                {insights.trends.improving.map((item, idx) => (
                                                    <li key={idx} className="text-sm text-muted-foreground">• {item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {insights?.trends?.declining?.length > 0 && (
                                        <div>
                                            <p className="text-sm font-medium text-red-600 flex items-center gap-1">
                                                <TrendingDown className="h-4 w-4" /> Declining
                                            </p>
                                            <ul className="mt-1 space-y-1">
                                                {insights.trends.declining.map((item, idx) => (
                                                    <li key={idx} className="text-sm text-muted-foreground">• {item}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(!insights?.trends?.improving?.length && !insights?.trends?.declining?.length) && (
                                        <p className="text-muted-foreground text-center py-4">No trend data available</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Next Steps */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-blue-500" />
                                    Recommended Next Steps
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {insights?.next_steps?.length > 0 ? (
                                    <ol className="space-y-2">
                                        {insights.next_steps.map((step, idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                                                    {idx + 1}
                                                </span>
                                                <span>{step}</span>
                                            </li>
                                        ))}
                                    </ol>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No recommendations available</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Alerts Tab */}
                <TabsContent value="alerts" className="space-y-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-orange-500" />
                                AI-Prioritized Alerts
                            </CardTitle>
                            <CardDescription>
                                {prioritizedAlerts?.summary || 'Alerts sorted by severity and potential impact'}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {prioritizedAlerts?.immediate_actions?.length > 0 && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                                    <p className="font-medium text-red-800 dark:text-red-200 mb-2">Immediate Actions Required:</p>
                                    <ul className="space-y-1">
                                        {prioritizedAlerts.immediate_actions.map((action, idx) => (
                                            <li key={idx} className="text-sm text-red-700 dark:text-red-300">• {action}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {prioritizedAlerts?.prioritized_alerts?.length > 0 ? (
                                <div className="space-y-3">
                                    {prioritizedAlerts.prioritized_alerts.slice(0, 10).map((alert, idx) => (
                                        <div key={idx} className="p-3 border rounded-lg">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="font-medium">{alert.title || alert.message || `Alert ${idx + 1}`}</p>
                                                    {alert.impact_assessment && (
                                                        <p className="text-sm text-muted-foreground mt-1">{alert.impact_assessment}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {alert.priority_score && (
                                                        <Badge variant="outline">Score: {alert.priority_score}/10</Badge>
                                                    )}
                                                </div>
                                            </div>
                                            {alert.recommended_action && (
                                                <p className="text-sm mt-2 text-primary">→ {alert.recommended_action}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                                    <p className="text-lg font-medium">No Active Alerts</p>
                                    <p className="text-muted-foreground">Your infrastructure is running smoothly</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Passwords Tab */}
                <TabsContent value="passwords" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-l-4 border-l-primary">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Password Health Score</p>
                                <p className="text-3xl font-bold">{passwordAnalysis?.overall_score || 0}/100</p>
                                <Progress value={passwordAnalysis?.overall_score || 0} className="mt-2" />
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-orange-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Risk Level</p>
                                <div className="mt-1">
                                    {getSeverityBadge(passwordAnalysis?.risk_level || 'unknown')}
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-red-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">At-Risk Users</p>
                                <p className="text-3xl font-bold">{passwordAnalysis?.at_risk_users?.length || 0}</p>
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Lock className="h-5 w-5" />
                                Password Analysis
                            </CardTitle>
                            <CardDescription>{passwordAnalysis?.summary}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="font-medium mb-3">At-Risk Users</h4>
                                    {passwordAnalysis?.at_risk_users?.length > 0 ? (
                                        <div className="space-y-2">
                                            {passwordAnalysis.at_risk_users.map((user, idx) => (
                                                <div key={idx} className="p-2 bg-red-50 dark:bg-red-950 rounded">
                                                    <p className="font-medium text-sm">{user.user}</p>
                                                    <p className="text-xs text-muted-foreground">{user.risk_reason}</p>
                                                    <p className="text-xs text-primary mt-1">→ {user.recommendation}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">No at-risk users identified</p>
                                    )}
                                </div>
                                <div>
                                    <h4 className="font-medium mb-3">Recommendations</h4>
                                    {passwordAnalysis?.recommendations?.length > 0 ? (
                                        <ul className="space-y-2">
                                            {passwordAnalysis.recommendations.map((rec, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-sm">
                                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                                    {rec}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-muted-foreground text-sm">No recommendations</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Network Tab */}
                <TabsContent value="network" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-l-4 border-l-primary">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Network Health</p>
                                <p className="text-3xl font-bold">{networkAnalysis?.network_health_score || 0}/100</p>
                                <Progress value={networkAnalysis?.network_health_score || 0} className="mt-2" />
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-orange-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Risk Level</p>
                                <div className="mt-1">
                                    {getSeverityBadge(networkAnalysis?.risk_level || 'unknown')}
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-yellow-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Maintenance Needed</p>
                                <p className="text-3xl font-bold">{networkAnalysis?.maintenance_needed?.length || 0}</p>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Predicted Issues</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {networkAnalysis?.predictions?.length > 0 ? (
                                    <div className="space-y-3">
                                        {networkAnalysis.predictions.map((pred, idx) => (
                                            <div key={idx} className="p-3 bg-muted rounded-lg">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-medium">{pred.device}</p>
                                                        <p className="text-sm text-muted-foreground">{pred.issue}</p>
                                                    </div>
                                                    <Badge variant="outline">{pred.probability}</Badge>
                                                </div>
                                                <p className="text-xs mt-2">Timeframe: {pred.timeframe}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No issues predicted</p>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Upgrade Recommendations</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {networkAnalysis?.upgrade_recommendations?.length > 0 ? (
                                    <div className="space-y-3">
                                        {networkAnalysis.upgrade_recommendations.map((rec, idx) => (
                                            <div key={idx} className="p-3 bg-muted rounded-lg">
                                                <p className="font-medium">{rec.device}</p>
                                                <p className="text-sm">{rec.recommendation}</p>
                                                <p className="text-xs text-muted-foreground mt-1">Benefit: {rec.benefit}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No upgrades recommended</p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Compliance Tab */}
                <TabsContent value="compliance" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-l-4 border-l-primary">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Compliance Score</p>
                                <p className="text-3xl font-bold">{complianceReport?.overall_compliance_score || 0}/100</p>
                                <Progress value={complianceReport?.overall_compliance_score || 0} className="mt-2" />
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-blue-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Security Grade</p>
                                <div className="mt-1 text-3xl font-bold">
                                    {getGradeBadge(complianceReport?.security_grade || 'C')}
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-red-500">
                            <CardContent className="pt-6">
                                <p className="text-sm text-muted-foreground">Compliance Gaps</p>
                                <p className="text-3xl font-bold">{complianceReport?.compliance_gaps?.length || 0}</p>
                            </CardContent>
                        </Card>
                    </div>

                    {complianceReport?.executive_summary && (
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
                            <CardContent className="py-4">
                                <p className="font-medium">Executive Summary</p>
                                <p className="text-muted-foreground">{complianceReport.executive_summary}</p>
                            </CardContent>
                        </Card>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Key Findings</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {complianceReport?.key_findings?.length > 0 ? (
                                    <div className="space-y-3">
                                        {complianceReport.key_findings.map((finding, idx) => (
                                            <div key={idx} className="p-3 border rounded-lg">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-medium">{finding.category}</p>
                                                        <p className="text-sm text-muted-foreground">{finding.finding}</p>
                                                    </div>
                                                    {getSeverityBadge(finding.severity)}
                                                </div>
                                                <p className="text-sm text-primary mt-2">→ {finding.recommendation}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-center py-4">No findings</p>
                                )}
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Action Plans</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {complianceReport?.immediate_actions?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-red-600">Immediate</p>
                                        <ul className="mt-1 space-y-1">
                                            {complianceReport.immediate_actions.map((action, idx) => (
                                                <li key={idx} className="text-sm flex items-start gap-2">
                                                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                                                    {action}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {complianceReport?.["30_day_plan"]?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-yellow-600">30-Day Plan</p>
                                        <ul className="mt-1 space-y-1">
                                            {complianceReport["30_day_plan"].map((action, idx) => (
                                                <li key={idx} className="text-sm flex items-start gap-2">
                                                    <Clock className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                                                    {action}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {complianceReport?.["90_day_plan"]?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-green-600">90-Day Plan</p>
                                        <ul className="mt-1 space-y-1">
                                            {complianceReport["90_day_plan"].map((action, idx) => (
                                                <li key={idx} className="text-sm flex items-start gap-2">
                                                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                                    {action}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AIInsightsPanel;
